import { useEffect, useState } from "react";

/**
 * Tour de bienvenida interactivo: 10 pasos con spotlight + tooltip por panel.
 *
 * - Autostart la primera vez (localStorage `videotool.tourSeen`)
 * - Cerrable / salt­able / relanzable
 * - Cada paso apunta a un selector CSS; si no lo encuentra, salta al siguiente
 */

interface Step {
  title: string;
  body: string;
  target: string | null;     // selector CSS, o null para paso centrado
  side?: "top" | "bottom" | "left" | "right" | "center";
}

const STEPS: Step[] = [
  {
    title: "Bienvenido a Videotool 🎬",
    body: "Te enseño en 60 segundos las cosas más útiles. Pulsa Siguiente.",
    target: null,
    side: "center",
  },
  {
    title: "📁 Mediateca",
    body: "Sube tus vídeos, audios e imágenes arrastrándolos aquí o con el botón + Subir. Luego los arrastras al timeline.",
    target: ".panel-library",
    side: "right",
  },
  {
    title: "▶ Preview",
    body: "Reproduce lo que estás editando. Espacio = play / pausa. Arrastra los textos directamente con el ratón para reposicionarlos.",
    target: ".panel-preview",
    side: "bottom",
  },
  {
    title: "⚙ Inspector",
    body: "Al seleccionar un clip aquí ajustas su corte, efectos de color, volumen y transición de salida.",
    target: ".panel-inspector",
    side: "left",
  },
  {
    title: "🎬 Línea de tiempo",
    body: "Arrastra clips para moverlos. Tira de los bordes para recortar. C = cortar en playhead. Supr = borrar. Ctrl+rueda = zoom.",
    target: ".panel-timeline",
    side: "top",
  },
  {
    title: "✨ Transiciones (botón verde)",
    body: "Más de 130 transiciones con preview animado al pasar el ratón. Arrástralas al borde de un clip o usa el botón + entre dos clips contiguos.",
    target: '[data-tour="transitions"]',
    side: "bottom",
  },
  {
    title: "🎨 Efectos (botón naranja)",
    body: "20 presets de color: Cinematográfico, Vintage, Blockbuster, Beast, B&N… Cada uno muestra preview real de cómo va a quedar. Click = aplicar al clip seleccionado.",
    target: '[data-tour="effects"]',
    side: "bottom",
  },
  {
    title: "🎵 Música (botón morado)",
    body: "4 secciones: 6 tracks bundleados, búsqueda en Internet Archive (miles de tracks libres), 10 SFX, y tus uploads. ▶ para escuchar, + Timeline para añadir.",
    target: '[data-tour="music"]',
    side: "bottom",
  },
  {
    title: "✨ Subtítulos automáticos",
    body: "Si no tienes ningún clip seleccionado, en el inspector verás un botón para generar subtítulos automáticos en castellano (Whisper IA). Necesitas tener audio en el timeline.",
    target: ".panel-inspector",
    side: "left",
  },
  {
    title: "🚀 ¡Listo para empezar!",
    body: "Pulsa el botón ? arriba para relanzar este tour cuando quieras. Cualquier duda dile a tu Claude. ¡A editar!",
    target: null,
    side: "center",
  },
];

const STORAGE_KEY = "videotool.tourSeen";

export function shouldShowTour(): boolean {
  try {
    return !localStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
}

export function markTourSeen() {
  try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); }
  catch { /* localStorage puede no estar disponible */ }
}

export default function WelcomeTour(props: { onClose: () => void }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const current = STEPS[step];

  useEffect(() => {
    function updateRect() {
      if (!current.target) { setRect(null); return; }
      const el = document.querySelector(current.target);
      if (el) setRect(el.getBoundingClientRect());
      else setRect(null);  // si no existe, no spotlight pero seguimos
    }
    updateRect();
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [current]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") finish();
      if (e.key === "ArrowRight" || e.key === "Enter") next();
      if (e.key === "ArrowLeft") prev();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  function next() {
    if (step < STEPS.length - 1) setStep(step + 1);
    else finish();
  }
  function prev() { if (step > 0) setStep(step - 1); }
  function finish() { markTourSeen(); props.onClose(); }

  // Posicionar tooltip según rect + side
  const PAD = 16;
  const W = 360;
  let tipStyle: React.CSSProperties = {
    position: "fixed",
    width: W,
    background: "var(--bg-elev)",
    border: "2px solid var(--accent)",
    borderRadius: 10,
    padding: 18,
    boxShadow: "0 12px 40px rgba(0,0,0,0.6)",
    zIndex: 100001,
  };
  if (!rect || current.side === "center") {
    tipStyle = { ...tipStyle, left: "50%", top: "50%", transform: "translate(-50%, -50%)" };
  } else {
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    if (current.side === "bottom") {
      const left = Math.min(window.innerWidth - W - 20, Math.max(20, cx - W / 2));
      tipStyle = { ...tipStyle, left, top: rect.bottom + PAD };
    } else if (current.side === "top") {
      const left = Math.min(window.innerWidth - W - 20, Math.max(20, cx - W / 2));
      tipStyle = { ...tipStyle, left, bottom: window.innerHeight - rect.top + PAD };
    } else if (current.side === "right") {
      tipStyle = { ...tipStyle, left: rect.right + PAD, top: Math.max(20, cy - 80) };
    } else {  // left
      tipStyle = { ...tipStyle, right: window.innerWidth - rect.left + PAD, top: Math.max(20, cy - 80) };
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100000, pointerEvents: "auto" }}>
      {/* Backdrop oscuro con hueco para el target (spotlight via SVG mask) */}
      <svg style={{ position: "fixed", inset: 0, width: "100vw", height: "100vh", pointerEvents: "none" }}>
        <defs>
          <mask id="tourMask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - 6} y={rect.top - 6}
                width={rect.width + 12} height={rect.height + 12}
                rx={8} fill="black"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.72)" mask="url(#tourMask)" />
        {rect && (
          <rect
            x={rect.left - 6} y={rect.top - 6}
            width={rect.width + 12} height={rect.height + 12}
            rx={8} fill="none"
            stroke="var(--accent)" strokeWidth={3}
            strokeDasharray="6 6"
            style={{ animation: "tourPulse 1.6s ease-in-out infinite" }}
          />
        )}
      </svg>

      {/* Permitir cerrar haciendo click en el backdrop fuera del tip */}
      <div
        onClick={finish}
        style={{ position: "fixed", inset: 0, cursor: "pointer", pointerEvents: "auto" }}
      />

      <div style={tipStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                       marginBottom: 8 }}>
          <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
            Paso {step + 1} de {STEPS.length}
          </span>
          <button onClick={finish} style={{ fontSize: 11, padding: "2px 8px" }}>
            Saltar tour
          </button>
        </div>
        <h3 style={{ margin: "0 0 8px", fontSize: 18 }}>{current.title}</h3>
        <p style={{ margin: "0 0 16px", lineHeight: 1.5, color: "var(--text)" }}>
          {current.body}
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {step > 0 && (
            <button onClick={prev} style={{ fontSize: 13 }}>← Atrás</button>
          )}
          <button onClick={next} className="primary" style={{ fontSize: 13, fontWeight: 600,
                                                                  padding: "6px 16px" }}>
            {step === STEPS.length - 1 ? "Empezar a editar ✨" : "Siguiente →"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes tourPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
